/**
 * IMPORTANT: Environment-Specific sys_ids
 *
 * These sys_ids are hardcoded values from ServiceNow production instances.
 * They may differ across environments (dev, test, prod) or custom instances.
 *
 * If AI Agent features fail silently or strategies/tools are not found:
 * 1. Verify these sys_ids match your target ServiceNow instance
 * 2. Query your instance to get the correct sys_ids:
 *    - Strategies: Navigate to sn_aia_strategy.list and check sys_id column
 *    - Tools: Navigate to sn_aia_tool.list and check sys_id column
 * 3. Update the constants below with your instance-specific values
 *
 * Note: OOB (Out-of-Box) records typically have consistent sys_ids across instances,
 * but this is not guaranteed. Always verify for your specific environment.
 */

/**
 * Default AI Agent strategy sys_id (ReAct strategy)
 * To verify: Navigate to sn_aia_strategy.list?sysparm_query=name=ReAct
 */
export const REACT_STRATEGY_SYS_ID = 'f0bff21f9f13c6108f431597d90a1c74'

/**
 * ServiceNow Out-of-Box (OOB) Web Search tool sys_id
 * To verify: Navigate to sn_aia_tool.list?sysparm_query=name=Web Search
 */
export const WEB_SEARCH_TOOL_SYS_ID = '576cf8eaffd922101dbaffffffffff66'

// Default inputs for Web Search tool
export const WEB_SEARCH_DEFAULT_INPUTS = JSON.stringify([
    {
        name: 'search_type',
        value: 'ai_answers',
        metadata: {
            _MODE_: 'user',
        },
    },
])

/**
 * Mapping of AI Agent strategy names to their sys_ids
 *
 * ENVIRONMENT-SPECIFIC: These sys_ids may vary across ServiceNow instances.
 *
 * To verify/update for your instance:
 * 1. Navigate to: sn_aia_strategy.list
 * 2. For each strategy, note the 'name' and 'sys_id' columns
 * 3. Update this mapping if sys_ids don't match
 *
 * Example query to export all strategies:
 * SELECT name, sys_id FROM sn_aia_strategy ORDER BY name
 */
export const STRATEGY_NAME_TO_SYS_ID_MAP: { [key: string]: string } = {
    'AIA - Conversational': '3f258fb9ffa8a210d84cffffffffff7a',
    'Base Planner': 'a43ada98ff1422106c36ffffffffff46',
    'Batch Planner': '7250a4099318e2505c21b7cd1dba10f7',
    'External Agent Executor': 'de4aa3e60bd02210175740976877b262',
    'Hierarchical ReAct': '18a2de41ff632210309fffffffffff90',
    Quick: '8282596347d716103b6f9402516d4399',
    ReAct: REACT_STRATEGY_SYS_ID,
    'ReActive Planner': 'b264c470ff1022106c36ffffffffff67',
    'Swarm Planner': '4b994008ff4c7210309fffffffffffb3',
    'Unified Planner': 'cfca2666476a22106480e5e5316d43f2',
}

/**
 * Mapping of OOB (Out-of-Box) tool types to their sys_ids
 *
 * ENVIRONMENT-SPECIFIC: These sys_ids reference OOB tools in ServiceNow.
 * When a tool type matches a key in this map, the SDK links to the existing
 * OOB tool instead of creating a new custom tool.
 *
 * To verify/add OOB tools for your instance:
 * 1. Navigate to: sn_aia_tool.list?sysparm_query=record_type=oob
 * 2. Note the 'type' and 'sys_id' for each OOB tool
 * 3. Add entries here to enable automatic linking
 *
 * Current OOB tools:
 * - web_automation: Web Search tool (searches the web for information)
 * - knowledge_graph: Knowledge Graph tool
 * - file_upload: File Uploader tool
 * - rag: Search Retrieval tool
 */
export const OOB_TOOL_TYPE_TO_SYS_ID_MAP: { [key: string]: string } = {
    web_automation: WEB_SEARCH_TOOL_SYS_ID,
    knowledge_graph: '3b8c4f4fc3e022100e94313a05013167',
    file_upload: 'b064af48ffb822101dbaffffffffff20',
    rag: '8021ddea2b0d52101d72fb466e91bfd1',
}

/**
 * Mapping of memory category names (as used in the Fluent API) to their sys_ids
 */
export const MEMORY_CATEGORY_TO_SYS_ID_MAP: Record<string, string> = {
    device_and_software: '7634aaae43db52107c4d14020ab8f294',
    meetings_and_events: '66442eae43db52107c4d14020ab8f26f',
    projects: '6b49e262431312107c4d14020ab8f2a9',
    workplace: '5c446eae43db52107c4d14020ab8f218',
}

export const OOB_CHANNEL_MAP = {
    'Now Assist Panel': 'b023fcb3537321106167ddeeff7b12a8',
}

export const CHANNEL_SYS_ID_TO_NAME_MAP: { [key: string]: string } = Object.fromEntries(
    Object.entries(OOB_CHANNEL_MAP).map(([name, sysId]) => [sysId, name])
)

/**
 * Reverse mapping from category sys_ids back to Fluent API category names
 */
export const SYS_ID_TO_MEMORY_CATEGORY_MAP: Record<string, string> = {
    '7634aaae43db52107c4d14020ab8f294': 'device_and_software',
    '66442eae43db52107c4d14020ab8f26f': 'meetings_and_events',
    '6b49e262431312107c4d14020ab8f2a9': 'projects',
    '5c446eae43db52107c4d14020ab8f218': 'workplace',
}

export const SN_AI_AGENT = 'sn_aia_agent'
export const SN_AIA_LTM_CATEGORY_MAPPING = 'sn_aia_ltm_category_mapping'
export const SN_AI_AGENT_CONFIG = 'sn_aia_agent_config'
export const SN_AI_AGENT_VERSION = 'sn_aia_version'
export const SN_AI_AGENT_TOOL = 'sn_aia_tool'
export const SN_AI_AGENT_TOOL_M2M = 'sn_aia_agent_tool_m2m'
export const SYS_SECURITY_ACL = 'sys_security_acl'
export const SYS_SECURITY_ACL_ROLE = 'sys_security_acl_role'
export const SYS_AGENT_ACCESS_ROLE_CONFIG = 'sys_agent_access_role_configuration'
export const SYS_AGENT_ACCESS_ROLE_MAPPING = 'sys_agent_access_role_mapping'
export const SYS_USER_ROLE = 'sys_user_role'

/**
 * Security attribute sys_id for "user is authenticated" ACL condition
 * To verify: Navigate to sys_security_acl_attribute.list?sysparm_query=name=user_is_authenticated
 */
export const USER_IS_AUTHENTICATED = '239bdf0c47d23110ba46ff58936d4330'

/**
 * Role sys_id assigned to sys_security_acl_role when securityAcl.type is 'Public'
 */
export const PUBLIC_ACCESS_ROLE_SYS_ID = 'b0593b350a0a0aa7001d689e4542dc28'

/** Default ACL field values for generated sys_security_acl records */
export const ACL_DEFAULTS = {
    operation: 'execute',
    active: true,
    adminOverrides: true,
    advanced: false,
    decisionType: 'allow',
    localOrExisting: 'Existing',
} as const
export const SN_AIA_TRIGGER_CONFIG = 'sn_aia_trigger_configuration'
export const SN_AIA_TRIGGER_MAPPING = 'sn_aia_trigger_agent_usecase_m2m'

/**
 * ACL type sys_id for Generative AI Agent
 * Used to query ACLs that control access to AI Agents
 * To verify: Navigate to sys_security_acl_type.list?sysparm_query=name=Generative AI Agent
 */
export const GEN_AI_AGENT_TYPE = 'fc55e9f79f8222109570e67e8a0a1c34'

/**
 * Mapping of tool types to their target document tables
 */
export const TOOL_TYPE_TO_TARGET_TABLE_MAP: { [key: string]: string } = {
    action: 'sys_hub_action_type_definition',
    capability: 'sn_nowassist_skill_config',
    subflow: 'sys_hub_flow',
    catalog: 'sc_cat_item',
    crud: 'sn_aia_tool',
    script: 'sn_aia_tool',
    topic_block: 'sys_cs_topic',
    desktop_automation: 'sn_aia_tool',
    deep_research: 'sn_aia_tool',
    file_upload: 'sn_aia_tool',
    rag: 'sn_aia_tool',
    mcp: 'sn_aia_tool',
    knowledge_graph: 'sn_aia_tool',
    topic: 'sys_cs_topic',
    web_automation: 'sn_aia_tool',
}

/**
 * Default context processing script for AI Agents
 * This script is used as the default when generating XML records.
 * During transform, if the script matches this default, it will be omitted from the generated TypeScript.
 */
export const DEFAULT_CONTEXT_PROCESSING_SCRIPT = `/*
 * Available Variables:
 * - task: The task provided to agent to solve either by orchestrator or user(incase of only agent execution i.e agent is invoked as seperate entity instead from a workflow).
 * - user_utterance: The user utterance provided. and task = user_utterance in case of individual agent execution.
 * - agent_id: The agent id.
 * - context: An object that carries additional contextual information for the agent.
 * It always includes a "pageContext" and may include a "triggerContext",
 * depending on how and from where the agent is invoked.
 *
 * Behavior by invocation type:
 * ---------------------------------
 * - When invoked via the startAiAgentConversation API:
 * If a "contextMemory" parameter is provided in the API request,
 * it will be available here as the "triggerContext".
 * The "pageContext" is always present in this call, regardless
 * of whether the agent is executed individually or as part of a use case.
 * Example:
 * {
 * pageContext: { ... },
 * triggerContext: { ... } // Derived from contextMemory (if passed)
 * }
 *
 * - When invoked as part of a use case or workflow:
 * The "pageContext" and "triggerContext" objects available here
 * may have been *modified* by the use case's context processing script.
 * This means the use case may alter, extend, or overwrite values
 * from the original "pageContext" or "triggerContext"
 * before the context reaches the agent.
 * If the use case was originally triggered via the API,
 * the initial "triggerContext" is still derived from the
 * "contextMemory" parameter, but its contents may have been updated
 * by the use case logic.
 *
 * - When executed directly (standalone agent execution):
 * The "pageContext" will always be present (default or empty),
 * and the "triggerContext" will only be populated if explicitly
 * passed through the API call.
 * Return an modified context like: { pageContext : {},triggerContext:{} }
 */
(function(task, user_utterance, agent_id, context) {
	return {
		pageContext : context?.pageContext,
		triggerContext : context?.triggerContext
	};
})(task,user_utterance, agent_id, context);`
